import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Checklist as CountIcon,
  DeleteOutline as DeleteIcon,
  PlaylistAdd as BulkLoadIcon,
  RateReview as ReviewIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import stockCountService from '../../../../services/stockCountService';
import inventoryItemService, { type InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import venueLocationService from '../../../../services/venueLocationService';
import type { StockCount, StockCountLine } from '../../../../types/venue';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatBottleSize = (ml?: number | null): string => {
  if (!ml) return '';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
};

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Component ─────────────────────────────────────────────────────────────────

const StockCountEntry: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, venueName } = useVenue();
  const { countId } = useParams<{ countId: string }>();
  const backPath = `/admin/venues/${venueId}/stock-counts`;
  const reviewPath = `/admin/venues/${venueId}/stock-counts/${countId}/review`;

  // ── Core data ─────────────────────────────────────────────────────────────────
  const [count, setCount] = useState<StockCount | null>(null);
  const [lines, setLines] = useState<StockCountLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [locationName, setLocationName] = useState('');

  // ── Header edit (draft only) ──────────────────────────────────────────────────
  const [headerForm, setHeaderForm] = useState({ countedAt: '', countedBy: '', notes: '' });
  const [headerDirty, setHeaderDirty] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);

  // ── Search & add line (draft only) ───────────────────────────────────────────
  const [searchValue, setSearchValue] = useState<InventoryItemWithBalance | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState<InventoryItemWithBalance[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingLine, setAddingLine] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // ── Bulk load (draft only) ────────────────────────────────────────────────────
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);

  // ── Per-line counted qty (controlled inputs) ──────────────────────────────────
  const [localCounts, setLocalCounts] = useState<Record<string, string>>({});
  const [savingLines, setSavingLines] = useState<Set<string>>(new Set());
  const [lineError, setLineError] = useState<string | null>(null);
  const [removingLines, setRemovingLines] = useState<Set<string>>(new Set());

  // ── Load ──────────────────────────────────────────────────────────────────────
  const loadCount = useCallback(async () => {
    if (!venueId || !countId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { count: c, lines: l } = await stockCountService.getCount(venueId, countId);
      setCount(c);
      setLines(l);
      setHeaderForm({
        countedAt: c.countedAt ?? '',
        countedBy: c.countedBy ?? '',
        notes: c.notes ?? '',
      });
      const initial: Record<string, string> = {};
      l.forEach(line => {
        initial[line._id] = line.countedQty !== null ? String(line.countedQty) : '';
      });
      setLocalCounts(initial);
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? err?.message ?? 'Failed to load stock count');
    } finally {
      setLoading(false);
    }
  }, [venueId, countId]);

  useEffect(() => { loadCount(); }, [loadCount]);

  // Load location name for scope display
  useEffect(() => {
    if (!count?.locationId || !venueId) return;
    venueLocationService.getLocation(venueId, count.locationId)
      .then(loc => setLocationName(loc.name))
      .catch(() => {});
  }, [count?.locationId, venueId]);

  // ── Header save ───────────────────────────────────────────────────────────────
  const saveHeader = async () => {
    if (!count || !countId || !venueId || !headerDirty) return;
    setHeaderSaving(true);
    setHeaderError(null);
    try {
      const updated = await stockCountService.updateCount(venueId, countId, {
        countedAt: headerForm.countedAt || null,
        countedBy: headerForm.countedBy,
        notes: headerForm.notes || undefined,
      });
      setCount(updated);
      setHeaderDirty(false);
    } catch (err: any) {
      setHeaderError(err?.response?.data?.message ?? 'Failed to save details');
    } finally {
      setHeaderSaving(false);
    }
  };

  // ── Inventory item search ─────────────────────────────────────────────────────
  const debouncedSearch = useRef(
    debounce(async (q: string, locId: string | null) => {
      if (q.trim().length < 2) { setSearchOptions([]); setSearchLoading(false); return; }
      setSearchLoading(true);
      try {
        const result = await inventoryItemService.getItems(
          venueId, 1, 20, 'displayName', 'asc', q,
          locId !== null ? locId : undefined,
        );
        // For no-location scope, filter client-side to items with no locationId
        const items = locId === null
          ? result.data.filter(item => !item.locationId)
          : result.data;
        setSearchOptions(items);
      } catch {
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchInput = (_: React.SyntheticEvent, value: string) => {
    setSearchInput(value);
    if (value.trim().length < 2) { setSearchOptions([]); setSearchLoading(false); debouncedSearch.cancel(); return; }
    setSearchLoading(true);
    debouncedSearch(value, count?.locationId ?? null);
  };

  const handleAddLine = async (item: InventoryItemWithBalance) => {
    if (!countId || !venueId) return;
    setAddingLine(true);
    setAddError(null);
    try {
      const newLine = await stockCountService.addLine(venueId, countId, item._id);
      setLines(prev => [...prev, newLine]);
      setLocalCounts(prev => ({ ...prev, [newLine._id]: '' }));
      setSearchValue(null);
      setSearchInput('');
      setSearchOptions([]);
    } catch (err: any) {
      setAddError(err?.response?.data?.message ?? err?.message ?? 'Failed to add item');
    } finally {
      setAddingLine(false);
    }
  };

  // ── Bulk load ─────────────────────────────────────────────────────────────────
  const handleBulkLoad = async () => {
    if (!countId || !venueId) return;
    setBulkLoading(true);
    setBulkMessage(null);
    setAddError(null);
    try {
      const result = await stockCountService.bulkLoadLines(venueId, countId);
      if (result.added > 0) {
        const existingIds = new Set(lines.map(l => l._id));
        const newLines = result.lines.filter(l => !existingIds.has(l._id));
        setLines(prev => [...prev, ...newLines]);
        const newLocal: Record<string, string> = {};
        newLines.forEach(l => { newLocal[l._id] = l.countedQty !== null ? String(l.countedQty) : ''; });
        setLocalCounts(prev => ({ ...prev, ...newLocal }));
        setBulkMessage(`${result.added} item${result.added !== 1 ? 's' : ''} added.`);
      } else {
        setBulkMessage('All items in this scope are already on the count.');
      }
    } catch (err: any) {
      setAddError(err?.response?.data?.message ?? err?.message ?? 'Bulk load failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Per-line editing ──────────────────────────────────────────────────────────
  const handleCountChange = (lineId: string, value: string) => {
    setLocalCounts(prev => ({ ...prev, [lineId]: value }));
    setLineError(null);
  };

  const handleCountBlur = async (lineId: string) => {
    if (!venueId || !countId) return;
    const raw = localCounts[lineId] ?? '';
    const num = raw.trim() === '' ? null : parseInt(raw, 10);

    const line = lines.find(l => l._id === lineId);
    if (!line) return;
    const unchanged = num === line.countedQty || (num === null && line.countedQty === null);
    if (unchanged) return;

    setSavingLines(prev => new Set(prev).add(lineId));
    try {
      const updated = await stockCountService.updateLine(venueId, countId, lineId, num);
      setLines(prev => prev.map(l => l._id === lineId ? updated : l));
    } catch (err: any) {
      setLineError(err?.response?.data?.message ?? 'Failed to save line');
    } finally {
      setSavingLines(prev => { const s = new Set(prev); s.delete(lineId); return s; });
    }
  };

  const handleRemoveLine = async (lineId: string) => {
    if (!venueId || !countId) return;
    setRemovingLines(prev => new Set(prev).add(lineId));
    try {
      await stockCountService.removeLine(venueId, countId, lineId);
      setLines(prev => prev.filter(l => l._id !== lineId));
      setLocalCounts(prev => { const n = { ...prev }; delete n[lineId]; return n; });
    } catch (err: any) {
      setLineError(err?.response?.data?.message ?? 'Failed to remove line');
      setRemovingLines(prev => { const s = new Set(prev); s.delete(lineId); return s; });
    }
  };

  // ── Lot-tracked detection ─────────────────────────────────────────────────────
  const [hasLotTrackedLines, setHasLotTrackedLines] = useState(false);

  useEffect(() => {
    if (!venueId || lines.length === 0) { setHasLotTrackedLines(false); return; }

    // Fast path: if any snapshot already carries lotTracked, no fetch needed.
    if (lines.some(l => l.snapshot.lotTracked === true)) {
      setHasLotTrackedLines(true);
      return;
    }

    // Slow path: fetch each unique item to check lotTracked.
    const uniqueIds = Array.from(new Set(lines.map(l => l.inventoryItemId)));
    Promise.all(uniqueIds.map(id => inventoryItemService.getItem(venueId, id).catch(() => null)))
      .then(items => {
        setHasLotTrackedLines(items.some(item => item?.lotTracked === true));
      });
  }, [venueId, lines]);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const isDraft = count?.status === 'draft';

  const getLiveVariance = (line: StockCountLine): number | null => {
    const raw = localCounts[line._id] ?? '';
    if (raw.trim() === '') return null;
    const n = parseInt(raw, 10);
    if (isNaN(n)) return null;
    return n - line.expectedQty;
  };

  const scopeLabel = count?.locationId
    ? (locationName || count.locationId)
    : venueName;

  const countedCount = lines.filter(l => (localCounts[l._id] ?? '').trim() !== '').length;

  // ── Loading / error states ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !count) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Stock Counts
        </Button>
        <Alert severity="error">{loadError ?? 'Count not found'}</Alert>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Nav bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Stock Counts
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CountIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600}>{scopeLabel}</Typography>
          </Box>
          <Chip
            label={count.status === 'posted' ? 'Posted' : 'Draft'}
            size="small"
            color={count.status === 'posted' ? 'success' : 'default'}
          />
        </Box>
        {isDraft && lines.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<ReviewIcon />}
            onClick={() => navigate(reviewPath)}
            sx={{ textTransform: 'none' }}
          >
            Review & Post Stock Count
          </Button>
        )}
      </Box>

      {/* Header details panel */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {isDraft ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">Count Details</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Count Date"
                type="date"
                value={headerForm.countedAt}
                onChange={e => { setHeaderForm(f => ({ ...f, countedAt: e.target.value })); setHeaderDirty(true); }}
                size="small"
                sx={{ width: 180 }}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="When the physical count happened"
              />
              <TextField
                label="Counted By"
                value={headerForm.countedBy}
                onChange={e => { setHeaderForm(f => ({ ...f, countedBy: e.target.value })); setHeaderDirty(true); }}
                size="small"
                sx={{ width: 200 }}
              />
              <TextField
                label="Notes"
                value={headerForm.notes}
                onChange={e => { setHeaderForm(f => ({ ...f, notes: e.target.value })); setHeaderDirty(true); }}
                size="small"
                sx={{ flex: 1, minWidth: 200 }}
                placeholder="Optional"
              />
            </Box>
            {headerError && <Alert severity="error" sx={{ py: 0.5 }}>{headerError}</Alert>}
            {headerDirty && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={headerSaving ? <CircularProgress size={14} /> : <SaveIcon />}
                  disabled={headerSaving}
                  onClick={saveHeader}
                  sx={{ textTransform: 'none' }}
                >
                  {headerSaving ? 'Saving…' : 'Save Count'}
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          // Posted: read-only header
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Scope</Typography>
              <Typography variant="body2" fontWeight={500}>{scopeLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Count Date</Typography>
              <Typography variant="body2">{formatDate(count.countedAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Counted By</Typography>
              <Typography variant="body2">{count.countedBy || '—'}</Typography>
            </Box>
            {count.postedAt && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Posted</Typography>
                <Typography variant="body2">{formatDate(count.postedAt)}</Typography>
              </Box>
            )}
            {count.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Notes</Typography>
                <Typography variant="body2">{count.notes}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Add items panel (draft only) */}
      {isDraft && (
        <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>Add Items</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Autocomplete
              value={searchValue}
              inputValue={searchInput}
              options={searchOptions}
              loading={searchLoading || addingLine}
              filterOptions={x => x}
              getOptionLabel={opt => opt.displayName}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              onInputChange={handleSearchInput}
              onChange={(_, newVal) => {
                if (newVal) handleAddLine(newVal);
              }}
              noOptionsText={searchInput.length < 2 ? 'Type at least 2 characters' : 'No items found in this scope'}
              sx={{ flex: 1, minWidth: 280 }}
              renderOption={(props, option) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                return (
                  <li key={key} {...rest}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.displayName}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Search inventory items"
                  size="small"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {(searchLoading || addingLine) && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <Button
              variant="outlined"
              startIcon={bulkLoading ? <CircularProgress size={16} /> : <BulkLoadIcon />}
              disabled={bulkLoading}
              onClick={handleBulkLoad}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 40 }}
            >
              Load all items in scope
            </Button>
          </Box>
          {addError && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }} onClose={() => setAddError(null)}>
              {addError}
            </Alert>
          )}
          {bulkMessage && (
            <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }} onClose={() => setBulkMessage(null)}>
              {bulkMessage}
            </Alert>
          )}
        </Paper>
      )}

      {/* Count sheet */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{
          px: 3, py: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Count Sheet</Typography>
            <Typography variant="caption" color="text.secondary">
              {lines.length} item{lines.length !== 1 ? 's' : ''}
              {isDraft && lines.length > 0 && ` · ${countedCount} counted`}
            </Typography>
          </Box>
        </Box>

        {lineError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setLineError(null)}>{lineError}</Alert>
          </Box>
        )}

        {hasLotTrackedLines && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="info">
              This count includes lot-tracked items. Count variances will be posted to unassigned stock, not individual lots. For lot-level accuracy, use a manual adjustment targeting the specific lot.
            </Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Item', 'Size', 'Location', 'Expected', 'Counted', 'Variance', ''].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25, whiteSpace: 'nowrap' }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <CountIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">
                        {isDraft
                          ? 'No items yet. Search above or click "Load all items in scope".'
                          : 'No lines on this count.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                lines.map(line => {
                  const liveVariance = getLiveVariance(line);
                  const isCounted = (localCounts[line._id] ?? '').trim() !== '';
                  const isSaving = savingLines.has(line._id);
                  const isRemoving = removingLines.has(line._id);

                  // In posted mode, use server variance. In draft mode, use live calc.
                  const displayVariance = isDraft ? liveVariance : line.varianceQty;

                  return (
                    <TableRow
                      key={line._id}
                      sx={{ opacity: isRemoving ? 0.4 : 1 }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {line.snapshot.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatBottleSize(line.snapshot.bottleSize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {line.snapshot.locationName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{line.expectedQty}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 90, py: 0.75 }}>
                        {isDraft ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField
                              value={localCounts[line._id] ?? ''}
                              onChange={e => handleCountChange(line._id, e.target.value)}
                              onBlur={() => handleCountBlur(line._id)}
                              type="number"
                              size="small"
                              sx={{
                                width: 80,
                                '& input': { textAlign: 'center', fontWeight: 600 },
                                opacity: isCounted ? 1 : 0.5,
                              }}
                              slotProps={{ htmlInput: { min: 0, step: 1 } }}
                              placeholder="—"
                            />
                            {isSaving && <CircularProgress size={14} />}
                          </Box>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {line.countedQty !== null ? line.countedQty : '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {displayVariance !== null ? (
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={
                              displayVariance < 0 ? 'error'
                                : displayVariance > 0 ? 'success.main'
                                  : 'text.secondary'
                            }
                          >
                            {displayVariance > 0 ? '+' : ''}{displayVariance}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isDraft && (
                          <Tooltip title="Remove line">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveLine(line._id)}
                                disabled={isRemoving}
                                sx={{ color: theme.palette.text.disabled, '&:hover': { color: 'error.main' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer — draft only, only show when there are lines */}
      {isDraft && lines.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
            Back to List
          </Button>
          <Button
            variant="contained"
            startIcon={<ReviewIcon />}
            onClick={() => navigate(reviewPath)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Review & Post Stock Count
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default StockCountEntry;
